import type { Language } from '@offer-catalog/services/i18n'
import type { Benefit } from '@/api/ddfs/ddfs'
import type { ProductBenefit as ProductBenefitType } from '@/types/catalog'
import { useQuery } from '@tanstack/react-query'
import { VpLoader, VpStarRating } from '@vtmn-play/react/headless'
import { useTranslation } from 'react-i18next'
import { fetchDdfsCollection } from '@/api/ddfs/ddfs'
import { queryKeys } from '@/query/queryKeys'
import './product-benefits.scss'

export interface ProductBenefitsProps {
  benefits: ProductBenefitType[]
}

export function ProductBenefit({ benefit, name }: Readonly<{ benefit: ProductBenefitType, name: string | undefined }>) {
  const { t } = useTranslation()
  const { value } = benefit
  const isStarRating = typeof value === 'number' && value >= 1 && value <= 5

  let stringValue = String(value)
  if (value === 6)
    stringValue = t('yes')
  else if (value === 7)
    stringValue = t('no')

  return (
    <div data-testid="benefit" className="benefit">
      <div>{name ?? <VpLoader size="small" />}</div>
      <div className="benefit-notation">
        {isStarRating
          ? (
              <VpStarRating
                score={value}
                aria-label={t('products-page.product-card.reviews.score', { score: value })}
              />
            )
          : (
              <div className="benefit-string-notation">{stringValue}</div>
            )}
      </div>
    </div>
  )
}

export function ProductBenefits({ benefits }: Readonly<ProductBenefitsProps>) {
  const { i18n, t } = useTranslation()
  const currentLanguage = i18n.language.split('-')[0] as Language
  const filteredBenefits = benefits?.filter(bene => bene.value >= 1 && bene.value <= 7) ?? []
  const ids = filteredBenefits.map(b => b.id)

  const { data } = useQuery({
    queryKey: queryKeys.benefitLabels(ids, currentLanguage),
    queryFn: async () => {
      return await fetchDdfsCollection<Benefit>('product_benefits', { 'id[]': ids.map(String) }, currentLanguage)
    },
  })

  return (
    <section
      data-testid="product-benefits_container"
      className="product-benefits-container"
    >
      <span className="sr-only">
        {t('products-page.custom-view-panel.fields.product-benefits')}
        :
        {' '}
      </span>
      <div className="product-benefits">
        {filteredBenefits.map(benefit => (
          <ProductBenefit
            key={benefit.id}
            benefit={benefit}
            name={data?.items?.find(item => item.id === Number(benefit.id))?.name}
          />
        ))}
      </div>
    </section>
  )
}
